import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, User, Eye, X } from 'lucide-react';
import { SanitizedContent } from '@/components/ui/sanitized-content';
import { Category, Tag, Author } from '@/types/article';

interface ArticlePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
  excerpt?: string;
  featuredImageUrl?: string;
  featuredImageAlt?: string;
  category?: Category;
  author?: Author;
  tags: Tag[];
  readingTime: number;
}

export const ArticlePreviewModal: React.FC<ArticlePreviewModalProps> = ({
  isOpen,
  onClose,
  title,
  content,
  excerpt,
  featuredImageUrl,
  featuredImageAlt,
  category,
  author,
  tags,
  readingTime
}) => {
  // Only process content when modal is open
  if (!isOpen) {
    return null;
  }

  const formatContent = (content: string): string => {
    if (!content) return '';
    
    // Add classes to existing HTML elements for proper styling (matching live site)
    let formattedContent = content
      .replace(/<h1[^>]*>/g, '<h1 class="article-main-heading">')
      .replace(/<h2[^>]*>/g, '<h2 class="article-sub-heading">')
      .replace(/<h3[^>]*>/g, '<h3 class="article-sub-heading-small">')
      .replace(/<p[^>]*>/g, '<p class="article-paragraph">')
      .replace(/<ul[^>]*>/g, '<ul class="article-bullets">')
      .replace(/<li[^>]*>/g, '<li class="article-bullet-item">');
    
    return formattedContent;
  };

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] h-[95vh] p-0">
        <div className="h-full flex flex-col">
          {/* Header with close button */}
          <DialogHeader className="px-6 py-4 border-b bg-muted/50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-primary" />
                <DialogTitle className="text-lg font-semibold">Article Preview</DialogTitle>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              This is how your article will appear on the live site
            </p>
          </DialogHeader>

          {/* Scrollable preview content */}
          <div className="flex-1 overflow-y-auto bg-background">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
              <article className="prose prose-lg max-w-none">
                {/* Article Header */}
                <header className="mb-8">
                  {category && (
                    <div className="mb-4">
                      <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                        {category.name}
                      </span>
                    </div>
                  )}
                  
                  <h1 className="text-4xl font-geist font-bold leading-tight mb-6 text-foreground">
                    {title || 'Untitled Article'}
                  </h1>

                  {/* Article Meta */}
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground border-b pb-6 mb-8 font-geist">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span className="font-medium">{author?.display_name || 'Author Name'}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{currentDate}</span>
                    </div>
                    {readingTime > 0 && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{readingTime} min read</span>
                      </div>
                    )}
                  </div>

                  {/* Featured Image */}
                  {featuredImageUrl && (
                    <div className="mb-8">
                      <img
                        src={featuredImageUrl}
                        alt={featuredImageAlt || title}
                        className="w-full h-auto rounded-lg shadow-lg"
                      />
                    </div>
                  )}

                  {/* Tags */}
                  {tags && tags.length > 0 && (
                    <div className="mb-8 pb-6 border-b">
                      <h3 className="text-lg font-semibold mb-3 font-geist">Tags</h3>
                      <div className="flex flex-wrap gap-2">
                        {tags.map((tag) => (
                          <span
                            key={tag.id}
                            className="inline-block bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </header>

                {/* Article Content */}
                {content ? (
                  <SanitizedContent 
                    content={formatContent(content)}
                    className="article-formatted-content"
                  />
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <p className="text-lg">No content yet. Start writing your article to see the preview.</p>
                  </div>
                )}

                {/* Critical CSS styles for article formatting (matching live site) */}
                <style dangerouslySetInnerHTML={{
                  __html: `
                    .article-formatted-content {
                      font-family: 'Geist', sans-serif !important;
                      line-height: 1.6 !important;
                      color: #374151 !important;
                    }
                    
                    .article-formatted-content .article-main-heading {
                      font-size: 2.25rem !important;
                      font-weight: 800 !important;
                      color: #111827 !important;
                      margin: 3rem 0 2rem 0 !important;
                      line-height: 1.2 !important;
                      font-family: 'Geist', sans-serif !important;
                      text-transform: none !important;
                    }
                    
                    .article-formatted-content .article-sub-heading {
                      font-size: 1.75rem !important;
                      font-weight: 700 !important;
                      color: #1f2937 !important;
                      margin: 2.5rem 0 1.5rem 0 !important;
                      line-height: 1.3 !important;
                      font-family: 'Geist', sans-serif !important;
                    }
                    
                    .article-formatted-content .article-sub-heading-small {
                      font-size: 1.5rem !important;
                      font-weight: 600 !important;
                      color: #374151 !important;
                      margin: 2rem 0 1rem 0 !important;
                      line-height: 1.4 !important;
                      font-family: 'Geist', sans-serif !important;
                    }
                    
                    .article-formatted-content .article-paragraph {
                      font-size: 1.125rem !important;
                      line-height: 1.7 !important;
                      color: #4b5563 !important;
                      margin: 0 0 1.5rem 0 !important;
                      font-family: 'Geist', sans-serif !important;
                      font-weight: 400 !important;
                    }
                    
                    .article-formatted-content .article-bullets {
                      margin: 1.5rem 0 2rem 0 !important;
                      padding-left: 1.5rem !important;
                      list-style: none !important;
                    }
                    
                    .article-formatted-content .article-bullet-item {
                      font-size: 1.125rem !important;
                      line-height: 1.6 !important;
                      color: #4b5563 !important;
                      margin: 0.75rem 0 !important;
                      padding-left: 1rem !important;
                      position: relative !important;
                      font-family: 'Geist', sans-serif !important;
                    }
                    
                    .article-formatted-content .article-bullet-item::before {
                      content: "•" !important;
                      color: #3b82f6 !important;
                      font-weight: bold !important;
                      position: absolute !important;
                      left: 0 !important;
                      top: 0 !important;
                    }

                    /* Internal links styling */
                    .article-formatted-content a {
                      color: #0ea5e9 !important;
                      text-decoration: underline !important;
                      transition: color 0.2s !important;
                    }

                    .article-formatted-content a:hover {
                      color: #0284c7 !important;
                    }
                  `
                }} />
              </article>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
